/**
 * Avatar a initiales. La teinte derive de l'identifiant, pas du nom : elle ne
 * change pas quand la personne est renommee, et deux homonymes restent
 * distinguables.
 */
import { initiales } from '../../lib/format';

export const TEINTES_AVATAR = [
  'bg-avatar-indigo-fond text-avatar-indigo-encre',
  'bg-avatar-vert-fond text-avatar-vert-encre',
  'bg-avatar-ambre-fond text-avatar-ambre-encre',
  'bg-avatar-ciel-fond text-avatar-ciel-encre',
  'bg-avatar-rose-fond text-avatar-rose-encre',
  'bg-avatar-violet-fond text-avatar-violet-encre',
] as const;

export type TeinteAvatar = (typeof TEINTES_AVATAR)[number];
export type TailleAvatar = 'sm' | 'md' | 'lg';

const TAILLES_AVATAR: Record<TailleAvatar, string> = {
  sm: 'h-6 w-6 text-2xs',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
};

/** Empreinte FNV-1a sur 32 bits : identique sur tous les postes et bien repartie. */
export function empreinte(texte: string): number {
  let valeur = 0x811c9dc5;
  for (let index = 0; index < texte.length; index += 1) {
    valeur ^= texte.charCodeAt(index);
    valeur = Math.imul(valeur, 0x01000193);
  }
  return valeur >>> 0;
}

export function teinteAvatar(identifiant: string): TeinteAvatar {
  return TEINTES_AVATAR[empreinte(identifiant) % TEINTES_AVATAR.length] ?? TEINTES_AVATAR[0];
}

export function Avatar({
  nom,
  identifiant,
  taille = 'md',
  decoratif = true,
  libelle = nom,
  className = '',
}: {
  nom: string;
  identifiant: string;
  taille?: TailleAvatar;
  /** Faux quand l'avatar est seul, sans le nom a cote : il est alors annonce. */
  decoratif?: boolean;
  /** Ce qu'annonce un avatar non decoratif (« Suivi par … ») ; le nom par defaut. */
  libelle?: string;
  className?: string;
}) {
  const accessibilite = decoratif
    ? { 'aria-hidden': true as const }
    : { role: 'img', 'aria-label': libelle, title: libelle };
  return (
    <span
      {...accessibilite}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold select-none ${TAILLES_AVATAR[taille]} ${teinteAvatar(identifiant)} ${className}`}
    >
      {initiales(nom) || '?'}
    </span>
  );
}
